var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { StudyAccessData, StudyStatus, StudyVersionStatus } from "healthex-common/study.dtos";
import { slugify } from "healthex-common/utils";
import { computed, observable, reaction } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { NamedRoutes } from "./NamedRoutes";
import { NavigationHelper } from "./NavigationHelper";
import { findMatchingNamedRouteParent } from "./RoutingUIManager";
const _studyManagerTabToRoutes = {
    PATIENT_MANAGEMENT: NamedRoutes.PROJECTS_PROJECT_MANAGER_PATIENT_MANAGEMENT,
    PATIENT_EXPERIENCE: NamedRoutes.PROJECTS_PROJECT_MANAGER_PATIENT_EXPERIENCE,
    ANALYTICS: NamedRoutes.PROJECTS_PROJECT_MANAGER_ANALYTICS,
    DETAILS: NamedRoutes.PROJECTS_PROJECT_MANAGER_DETAILS,
};
const studyManagerTabToRoutes = _studyManagerTabToRoutes;
export class StudiesUIManager {
    constructor(routingUIManager, studyManager, authManager, studyRecruitmentManager) {
        this.routingUIManager = routingUIManager;
        this.studyManager = studyManager;
        this.authManager = authManager;
        this.studyRecruitmentManager = studyRecruitmentManager;
        this.studyManagerNavigationHelper = new NavigationHelper(this.routingUIManager, studyManagerTabToRoutes);
        this.studyToManage = NOT_LOADED;
        this.analyticsStats = NOT_LOADED;
        this.enrollmentMetrics = NOT_LOADED;
        this.whatsNew = NOT_LOADED;
        this.lastStatsReceivedDate = new Date();
        routingUIManager.registerRedirect(NamedRoutes.RESEARCH_STUDIES, () => NamedRoutes.PROJECTS);
        reaction(() => routingUIManager.currentRoute, async () => {
            await this.refreshStudyToManage();
            await this.refreshAnalyticsStatsAndEnrollmentMetrics();
            await this.refreshWhatsNew();
        }, { fireImmediately: true });
        reaction(() => [routingUIManager.currentRoute, this.studyToManage], async () => {
            this.redirectToInitialStudyManagerTab();
        }, { fireImmediately: true });
    }
    get currentStudyManagerTab() {
        return this.studyManagerNavigationHelper.currentNavigationItem;
    }
    get isStudyUsingIdentifiedData() {
        return (this.studyToManage !== NOT_LOADED &&
            !!this.studyToManage &&
            this.studyToManage.accessData === StudyAccessData.FULL_RECORD_IDENTIFIED);
    }
    get studyWithdrawnOrCompleted() {
        if (this.studyToManage === NOT_LOADED || this.studyToManage === null)
            return false;
        return this.studyToManage.status === StudyStatus.COMPLETED || this.studyToManage.status === StudyStatus.WITHDRAWN;
    }
    setCurrentStudyManagerTab(tab, recruitmentFilter) {
        var _a;
        if (this.studyToManage === NOT_LOADED)
            return;
        this.studyManagerNavigationHelper.navigateTo(tab, {
            projectId: (_a = this.studyToManage) === null || _a === void 0 ? void 0 : _a.id,
            filter: recruitmentFilter ? slugify(recruitmentFilter) : undefined,
        });
    }
    async refreshStudyToManage() {
        var _a;
        if (!findMatchingNamedRouteParent(this.routingUIManager.currentRoute, [
            NamedRoutes.PROJECTS_PROJECT_MANAGER,
            NamedRoutes.PROJECTS_ADD_PATIENTS_METHOD,
        ])) {
            this.studyToManage = NOT_LOADED;
            return;
        }
        const studyId = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.projectId;
        if (studyId) {
            this.studyToManage = await this.studyManager.getStudy(studyId, StudyVersionStatus.ACTIVE);
        }
    }
    async resumeRecruitment() {
        if (this.studyToManage === NOT_LOADED || !this.studyToManage)
            return;
        await this.studyManager.updateStudy(this.studyToManage, { paused: false, pauseReason: null });
    }
    redirectToInitialStudyManagerTab() {
        var _a;
        if (((_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) !== NamedRoutes.PROJECTS_PROJECT_MANAGER.routeName) {
            return;
        }
        if (this.studyToManage === NOT_LOADED) {
            return;
        }
        if (this.studyToManage === null) {
            return;
        }
        this.studyManagerNavigationHelper.redirectTo("PATIENT_MANAGEMENT", { projectId: this.studyToManage.id });
    }
    async refreshAnalyticsStatsAndEnrollmentMetrics() {
        var _a;
        if (this.studyToManage === NOT_LOADED || !this.studyToManage) {
            return;
        }
        if (((_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) !== NamedRoutes.PROJECTS_PROJECT_MANAGER_ANALYTICS.routeName) {
            return;
        }
        this.lastStatsReceivedDate = new Date();
        this.analyticsStats = await this.studyManager.fetchOverviewStats(this.studyToManage);
        this.enrollmentMetrics = await this.studyManager.fetchEnrollmentMetrics(this.studyToManage);
    }
    async refreshWhatsNew() {
        var _a;
        if (this.studyToManage === NOT_LOADED || !this.studyToManage) {
            return;
        }
        if (((_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) !== NamedRoutes.PROJECTS_PROJECT_MANAGER_ANALYTICS.routeName) {
            return;
        }
        this.whatsNew = await this.studyManager.fetchWhatsNew(this.studyToManage);
    }
    async editProject() {
        if (this.studyToManage === NOT_LOADED || !this.studyToManage)
            return;
        await this.studyManager.createStudyDraftIfNecessary(this.studyToManage.id);
        this.routingUIManager.navigateTo(NamedRoutes.PROJECTS_ADD_PROJECT, { projectId: this.studyToManage.id });
    }
}
__decorate([
    observable
], StudiesUIManager.prototype, "studyToManage", void 0);
__decorate([
    observable
], StudiesUIManager.prototype, "analyticsStats", void 0);
__decorate([
    observable
], StudiesUIManager.prototype, "enrollmentMetrics", void 0);
__decorate([
    observable
], StudiesUIManager.prototype, "whatsNew", void 0);
__decorate([
    observable
], StudiesUIManager.prototype, "lastStatsReceivedDate", void 0);
__decorate([
    computed
], StudiesUIManager.prototype, "currentStudyManagerTab", null);
__decorate([
    computed
], StudiesUIManager.prototype, "isStudyUsingIdentifiedData", null);
__decorate([
    computed
], StudiesUIManager.prototype, "studyWithdrawnOrCompleted", null);
